(() => {
  if (customElements.get('bn-hero-carousel')) return;
  class HeroCarousel extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      const on = (target, event, fn, options = {}) => target.addEventListener(event, fn, { ...options, signal });
      this.slides = [...this.querySelectorAll('[data-bnhh-slide]')];
      this.dots = [...this.querySelectorAll('[data-bnhh-dot]')];
      this.index = 0;
      this.pendingIndex = 0;
      this.request = (this.request || 0) + 1;
      this.autoPending = false;
      this.pointer = null;
      this.interval = Number(this.dataset.autoplay) * 1000;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches || !Number.isFinite(this.interval) || this.interval < 5000;
      this.visible = false;
      this.focusWithin = this.contains(document.activeElement);
      this.status = this.querySelector('[data-bnhh-status]');
      this.sync();
      if (this.slides.length < 2) return;
      this.querySelector('[data-bnhh-controls]').hidden = false;
      this.dataset.carouselReady = 'true';
      this.resizeObserver = new ResizeObserver(() => this.positionControls());
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.querySelector('[data-bnhh-slide]').parentElement);
      on(window, 'resize', () => this.positionControls(), { passive: true });
      on(this, 'load', () => this.positionControls(), { capture: true });
      document.fonts?.ready.then(() => { if (this.isConnected) this.positionControls(); });
      this.positionControls();
      on(this, 'click', e => {
        const button = e.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-bnhh-previous')) this.go(this.pendingIndex - 1, true, false, e.detail === 0);
        if (button.hasAttribute('data-bnhh-next')) this.go(this.pendingIndex + 1, true, false, e.detail === 0);
        if (button.hasAttribute('data-bnhh-dot')) this.go(Number(button.dataset.bnhhDot), true, false, e.detail === 0);
      });
      on(this, 'keydown', e => {
        if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || e.target.closest('input,textarea,select,[contenteditable]')) return;
        const target = { ArrowLeft: this.pendingIndex - 1, ArrowRight: this.pendingIndex + 1, Home: 0, End: this.slides.length - 1 }[e.key];
        if (target === undefined) return;
        e.preventDefault();
        this.go(target, true, Boolean(e.target.closest('[data-bnhh-dot]')), true);
      });
      on(this, 'pointerdown', e => {
        if (e.isPrimary && e.button === 0) { this.paused = true; this.cancelAuto(); }
        if (!e.isPrimary || e.button !== 0 || e.target.closest('a,button,input,video,select')) return;
        this.pointer = { id: e.pointerId, x: e.clientX, y: e.clientY };
      }, { passive: true });
      on(window, 'pointerup', e => {
        const start = this.pointer;
        this.pointer = null;
        if (!start || start.id !== e.pointerId) return;
        const dx = e.clientX - start.x, dy = e.clientY - start.y;
        if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.go(this.pendingIndex + (dx < 0 ? 1 : -1), true);
      }, { passive: true });
      on(this, 'pointercancel', () => { this.pointer = null; }, { passive: true });
      on(this, 'dragstart', e => { if (e.target.tagName === 'IMG') e.preventDefault(); });
      on(this, 'mouseenter', () => this.cancelAuto());
      on(this, 'mouseleave', () => this.schedule());
      on(this, 'focusin', () => { this.focusWithin = true; this.cancelAuto(); });
      on(this, 'focusout', e => {
        if (!this.contains(e.relatedTarget)) {
          this.focusWithin = false;
          queueMicrotask(() => { if (this.isConnected) this.schedule(); });
        }
      });
      on(document, 'visibilitychange', () => { if (document.hidden) this.cancelAuto(); this.schedule(); });
      on(this.motion, 'change', () => { if (this.motion.matches) { this.paused = true; this.cancelAuto(); } this.sync(); this.schedule(); });
      on(document, 'shopify:block:select', e => {
        const n = this.slides.findIndex(slide => slide.dataset.blockId === e.detail?.blockId);
        if (n >= 0) this.go(n, true);
      });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.2;
        if (!this.visible) {
          this.cancelAuto();
          this.slides[this.index]?.querySelectorAll('video').forEach(video => video.pause());
        }
        this.schedule();
      }, { threshold: 0.2 });
      this.observer.observe(this);
      this.schedule();
    }
    disconnectedCallback() {
      this.stop();
      this.pointer = null;
      this.request++;
      this.abort?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.abort = null;
    }
    stop() { clearTimeout(this.timer); this.timer = null; }
    cancelAuto() {
      this.stop();
      if (this.autoPending) {
        this.autoPending = false;
        this.request++;
        this.pendingIndex = this.index;
      }
      this.syncPlayback();
    }
    canAdvance() {
      return this.isConnected && this.slides.length > 1 && Number.isFinite(this.interval) && this.interval >= 5000 &&
        !this.paused && !this.motion.matches && this.visible && !document.hidden &&
        !this.matches(':hover') && !this.focusWithin && !this.matches(':focus-within');
    }
    syncPlayback() {
      this.dataset.playbackState = this.slides.length < 2 ? 'static' : this.motion.matches ? 'reduced' :
        this.paused ? 'paused' : this.canAdvance() ? 'playing' : 'waiting';
    }
    schedule() {
      this.stop();
      if (this.canAdvance()) this.timer = setTimeout(() => this.go(this.index + 1, false), this.interval);
      this.syncPlayback();
    }
    async go(n, user, focusIndicator = false, keyboard = focusIndicator) {
      const focusOrigin = document.activeElement;
      this.stop();
      if (user) { this.paused = true; this.syncPlayback(); }
      n = (n + this.slides.length) % this.slides.length;
      this.pendingIndex = n;
      this.autoPending = !user;
      const request = ++this.request, target = this.slides[n];
      const template = target.querySelector('template[data-bnhh-media]');
      if (template) { template.replaceWith(template.content.cloneNode(true)); }
      const img = target.querySelector('img');
      if (img) img.loading = 'eager';
      if (img && !img.complete) {
        await Promise.race([img.decode().catch(() => {}), new Promise(resolve => setTimeout(resolve, 3000))]);
      }
      if (request !== this.request || !this.isConnected) return;
      this.autoPending = false;
      if (!user && !this.canAdvance()) {
        this.pendingIndex = this.index;
        this.schedule();
        return;
      }
      const keepKeyboardFocus = keyboard && document.activeElement === focusOrigin;
      const previous = this.slides[this.index];
      if (previous !== target) {
        if (previous.contains(document.activeElement)) this.dots[n]?.focus({ preventScroll: true });
        previous.querySelectorAll('video').forEach(video => video.pause());
      }
      this.index = n;
      this.sync();
      if (focusIndicator && keepKeyboardFocus) this.dots[n]?.focus({ preventScroll: true });
      if (keepKeyboardFocus) {
        const focusedDot = this.dots.find(dot => dot === document.activeElement);
        if (focusedDot?.matches(':focus-visible')) {
          const rect = focusedDot.getBoundingClientRect();
          const viewport = window.visualViewport;
          const viewportTop = viewport?.offsetTop || 0;
          const bottom = viewportTop + (viewport?.height || window.innerHeight);
          const header = document.querySelector('[data-bn-header]');
          const headerRect = header?.getBoundingClientRect();
          const headerStyle = header && window.getComputedStyle(header);
          const headerVisible = headerRect && headerRect.width > 0 && headerRect.height > 0 &&
            headerRect.top < bottom && headerRect.bottom > viewportTop &&
            headerRect.left < rect.right && headerRect.right > rect.left &&
            headerStyle.visibility === 'visible' && Number(headerStyle.opacity) > 0;
          const top = headerVisible ? Math.max(viewportTop, Math.min(headerRect.bottom, bottom - rect.height)) : viewportTop;
          const delta = rect.bottom > bottom ? rect.bottom - bottom : rect.top < top ? rect.top - top : 0;
          if (delta) window.scrollBy({ top: delta, behavior: 'instant' });
        }
      }
      if (user && this.status) this.status.textContent = `${target.getAttribute('aria-label')}. ${this.dataset.labelStopped || ''}`;
      this.schedule();
    }
    positionControls() {
      const controls = this.querySelector('[data-bnhh-controls]');
      const photo = this.slides?.[this.index]?.querySelector('.bnhh-photo');
      if (!controls || controls.hidden || !photo) return;
      const host = this.getBoundingClientRect(), image = photo.getBoundingClientRect();
      if (image.width <= 0 || image.height <= 0) return;
      const inset = parseFloat(getComputedStyle(this).getPropertyValue('--bnhh-dash-bottom')) || 36;
      // 44px hit target: the centered 4px dash ends 24px below the box top.
      controls.style.top = `${Math.max(0, Math.min(host.height - 44, image.bottom - host.top - inset - 24))}px`;
    }
    sync() {
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      this.syncPlayback();
      this.dots.forEach((dot, i) => {
        const active = i === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-pressed', String(active));
        if (active) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current');
      });
      this.positionControls();

    }
  }
  customElements.define('bn-hero-carousel', HeroCarousel);
})();
